import { Box, Button, Drawer, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { isRejectableStatus } from "../constants";
import { colors, spacing, typography } from "../../../../assets/styles/tokens";

interface RejectLeadsModalMobileProps {
  data: LeadsManagementData;
}

const TEXT_SX = {
  fontFamily: typography.Font_Family.$value,
  letterSpacing: "0%",
};

const BUTTON_SX = {
  ...TEXT_SX,
  flex: 1,
  height: 40,
  textTransform: "none",
  fontWeight: typography.Font_weight.Semibold.$value,
  fontSize: typography.Size.Text.md.$value,
  lineHeight: typography.Line_height.text.md.$value,
  borderRadius: spacing.Radius.xs.$value,
  boxShadow: "none",
};

// Bottom-sheet counterpart of Leads/RejectLeadsModal.tsx — same data and
// handlers, laid out per the mobile mockup (centred warning, full-width actions).
export default function RejectLeadsModalMobile({ data }: RejectLeadsModalMobileProps) {
  const { showRejectConfirm, selectedLeads, cancelBulkReject, confirmBulkReject, isRejecting, canRejectLeads } = data;

  const rejectableSelected = selectedLeads.filter((lead) => isRejectableStatus(lead.status));
  const brandCounts = new Map<string, number>();
  rejectableSelected.forEach((lead) => {
    brandCounts.set(lead.brand, (brandCounts.get(lead.brand) ?? 0) + 1);
  });
  const brandBreakdown = Array.from(brandCounts.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);

  const warningColor = colors.Warning[600].$value;
  const rowTextSx = {
    ...TEXT_SX,
    fontSize: typography.Size.Text.md.$value,
    lineHeight: typography.Line_height.text.md.$value,
    color: colors.GrayLightMode[900].$value,
  };

  return (
    <Drawer
      anchor="bottom"
      open={showRejectConfirm}
      onClose={cancelBulkReject}
      // Opened from the full-screen detail Dialog (zIndex.modal), which would
      // otherwise cover a Drawer (zIndex.drawer sits below modal).
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            px: spacing.Spacing.xl.$value,
            pt: spacing.Spacing.xl.$value,
            pb: spacing.Spacing["4xl"].$value,
          },
        },
      }}
    >
      <Box
        sx={{
          border: `1px solid ${warningColor}`,
          borderRadius: spacing.Radius.xs.$value,
          bgcolor: colors.Warning[25].$value,
          py: spacing.Spacing.md.$value,
          px: spacing.Spacing.lg.$value,
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.Spacing.xs.$value }}>
          <WarningIcon sx={{ fontSize: 16, color: warningColor }} />
          <Typography
            sx={{
              ...TEXT_SX,
              fontWeight: 500,
              fontSize: typography.Size.Text.md.$value,
              lineHeight: typography.Line_height.text.md.$value,
              color: warningColor,
            }}
          >
            This cannot be undone
          </Typography>
        </Box>
        <Typography
          sx={{
            ...TEXT_SX,
            fontWeight: 400,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            color: warningColor,
            mt: spacing.Spacing.xs.$value,
          }}
        >
          Rejected leads won't be sent to their brand.
        </Typography>
      </Box>

      <Box sx={{ mt: spacing.Spacing["4xl"].$value, maxHeight: 240, overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: spacing.Spacing.md.$value,
            py: spacing.Spacing.md.$value,
            borderBottom: `1px solid ${colors.GrayLightMode[200].$value}`,
          }}
        >
          <Typography sx={{ ...rowTextSx, fontWeight: 500 }}>Brand Name</Typography>
          <Typography sx={{ ...rowTextSx, fontWeight: 500 }}>Leads</Typography>
        </Box>
        {brandBreakdown.map(({ brand, count }, index) => (
          <Box
            key={brand}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: spacing.Spacing.md.$value,
              py: spacing.Spacing.md.$value,
              borderTop: index === 0 ? "none" : `1px solid ${colors.GrayLightMode[200].$value}`,
            }}
          >
            <Typography sx={{ ...rowTextSx, fontWeight: 400 }}>{brand}</Typography>
            <Typography sx={{ ...rowTextSx, fontWeight: 400 }}>{count}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: spacing.Spacing.xl.$value, mt: spacing.Spacing["5xl"].$value }}>
        <Button
          onClick={cancelBulkReject}
          disabled={isRejecting}
          sx={{ ...BUTTON_SX, color: colors.Brand[600].$value, "&:hover": { bgcolor: colors.Brand[25].$value } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmBulkReject}
          disabled={!canRejectLeads || isRejecting || rejectableSelected.length === 0}
          sx={{
            ...BUTTON_SX,
            bgcolor: colors.Brand[600].$value,
            "&:hover": { bgcolor: colors.Brand[700].$value, boxShadow: "none" },
            // Same as the detail drawer's Report button: keep the in-progress label legible.
            ...(isRejecting && {
              "&.Mui-disabled": { color: colors.Base.White.$value, bgcolor: colors.Brand[600].$value },
            }),
          }}
        >
          {isRejecting ? "Rejecting…" : "Confirm"}
        </Button>
      </Box>
    </Drawer>
  );
}
